import { format, subMonths } from 'date-fns';
import type {
  CustomerAnalyticsInsightsMarketMetricCompsDto,
  CustomerAnalyticsInsightsMarketPriceCompsDto,
  CustomerAnalyticsMarketRentPriceResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import type {
  CostPerSqFtChartDataPoint,
  PropertyViewCompsChartDataPoint,
  RentCompsAverages,
  RentCompsChartDataPoint,
} from './RentComps.types';

export const generateRentCompsChartData = (
  priceComps: Array<CustomerAnalyticsInsightsMarketPriceCompsDto> | undefined,
  marketAvgData: Array<CustomerAnalyticsMarketRentPriceResponseDto> | undefined,
  allCompNames: Array<string>,
): { chartData: Array<RentCompsChartDataPoint>; averages: RentCompsAverages } => {
  if (!priceComps?.length) {
    return { chartData: [], averages: { portfolioAverage: 0, competitorAverage: 0 } };
  }

  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const chartData: Array<RentCompsChartDataPoint> = [];

  priceComps.forEach((monthComps) => {
    const date = new Date(monthComps.date.year, monthComps.date.month - 1, 1);
    const formattedDate = format(date, 'MMM yyyy');
    const timestamp = date.getTime();

    const marketAverage =
      marketAvgData?.find((data) => data.date.year === date.getFullYear() && data.date.month === date.getMonth() + 1)
        ?.avgPrice ?? monthComps.marketAvgPrice;

    const item: RentCompsChartDataPoint = {
      name: formattedDate,
      timestamp,
      'Market average': null,
      'Market average month to date': null,
      'Month to date': null,
    };

    item[monthComps.selectedListing.listingName] = Math.round(monthComps.selectedListing.avgPrice);

    monthComps.comps.forEach((comp) => {
      if (allCompNames.includes(comp.listingName)) {
        item[comp.listingName] = Math.round(comp.avgPrice);
      }
    });

    if (formattedDate === currentDateFormatted) {
      item['Market average month to date'] = Math.round(marketAverage);
    } else {
      item['Market average'] = Math.round(marketAverage);
    }

    chartData.push(item);
  });

  chartData.sort((a, b) => a.timestamp - b.timestamp);

  const mtdIndex = chartData.findIndex((data) => data.name === currentDateFormatted);
  if (mtdIndex > 0) {
    const selectedName = priceComps[0].selectedListing.listingName;
    const prevData = chartData[mtdIndex - 1];
    const mtdData = chartData[mtdIndex];

    prevData['Market average month to date'] = prevData['Market average'];

    allCompNames.forEach((name) => {
      const mtdKey = `${name} month to date`;
      mtdData[mtdKey] = mtdData[name];
      prevData[mtdKey] = prevData[name];
      mtdData[name] = null;
    });

    const selectedMtdKey = `${selectedName} month to date`;
    mtdData[selectedMtdKey] = mtdData[selectedName];
    prevData[selectedMtdKey] = prevData[selectedName];
    mtdData[selectedName] = null;
  }

  const lastMonth = priceComps[priceComps.length - 1];
  const portfolioAverage = lastMonth?.selectedListing?.avgPrice ?? 0;
  const compPrices = lastMonth?.comps?.filter((c) => allCompNames.includes(c.listingName)).map((c) => c.avgPrice) ?? [];
  const competitorAverage = compPrices.length > 0 ? compPrices.reduce((a, b) => a + b, 0) / compPrices.length : 0;

  return {
    chartData,
    averages: { portfolioAverage, competitorAverage },
  };
};

export const generateCostPerSqFtChartData = (
  priceComps: Array<CustomerAnalyticsInsightsMarketPriceCompsDto> | undefined,
  allCompNames: Array<string>,
): { chartData: Array<CostPerSqFtChartDataPoint>; averages: RentCompsAverages } => {
  if (!priceComps?.length) {
    return { chartData: [], averages: { portfolioAverage: 0, competitorAverage: 0 } };
  }

  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const chartData: Array<CostPerSqFtChartDataPoint> = [];

  priceComps.forEach((monthComps) => {
    const date = new Date(monthComps.date.year, monthComps.date.month - 1, 1);
    const formattedDate = format(date, 'MMM yyyy');
    const timestamp = date.getTime();

    const allCosts = [monthComps.selectedListing, ...monthComps.comps].map((l) => l.costPerSqFoot).filter((c) => c > 0);
    const marketAvgCostPerSqFt =
      allCosts.length > 0 ? Math.round((allCosts.reduce((a, b) => a + b, 0) / allCosts.length) * 100) / 100 : null;

    const item: CostPerSqFtChartDataPoint = {
      name: formattedDate,
      timestamp,
      'Market average': null,
      'Market average month to date': null,
      'Month to date': null,
    };

    const selectedCostPerSqFt = Math.round(monthComps.selectedListing.costPerSqFoot * 100) / 100;
    item[monthComps.selectedListing.listingName] = selectedCostPerSqFt;

    monthComps.comps.forEach((comp) => {
      if (allCompNames.includes(comp.listingName)) {
        item[comp.listingName] = Math.round(comp.costPerSqFoot * 100) / 100;
      }
    });

    if (marketAvgCostPerSqFt !== null) {
      if (formattedDate === currentDateFormatted) {
        item['Market average month to date'] = marketAvgCostPerSqFt;
      } else {
        item['Market average'] = marketAvgCostPerSqFt;
      }
    }

    chartData.push(item);
  });

  chartData.sort((a, b) => a.timestamp - b.timestamp);

  const mtdIndex = chartData.findIndex((data) => data.name === currentDateFormatted);
  if (mtdIndex > 0) {
    const selectedName = priceComps[0].selectedListing.listingName;
    const prevData = chartData[mtdIndex - 1];
    const mtdData = chartData[mtdIndex];

    prevData['Market average month to date'] = prevData['Market average'];

    allCompNames.forEach((name) => {
      const mtdKey = `${name} month to date`;
      mtdData[mtdKey] = mtdData[name];
      prevData[mtdKey] = prevData[name];
      mtdData[name] = null;
    });

    const selectedMtdKey = `${selectedName} month to date`;
    mtdData[selectedMtdKey] = mtdData[selectedName];
    prevData[selectedMtdKey] = prevData[selectedName];
    mtdData[selectedName] = null;
  }

  const lastMonth = priceComps[priceComps.length - 1];
  const portfolioAverage = lastMonth?.selectedListing?.costPerSqFoot ?? 0;
  const compCosts =
    lastMonth?.comps?.filter((c) => allCompNames.includes(c.listingName)).map((c) => c.costPerSqFoot) ?? [];
  const competitorAverage = compCosts.length > 0 ? compCosts.reduce((a, b) => a + b, 0) / compCosts.length : 0;

  return {
    chartData,
    averages: {
      portfolioAverage: Math.round(portfolioAverage * 100) / 100,
      competitorAverage: Math.round(competitorAverage * 100) / 100,
    },
  };
};

export const generateViewCompsChartData = (
  metricComps: Array<CustomerAnalyticsInsightsMarketMetricCompsDto> | undefined,
  displayedCompNames: Array<string> = [],
): { chartData: Array<PropertyViewCompsChartDataPoint>; averageViewRanking: number } => {
  if (!metricComps?.length) {
    return { chartData: [], averageViewRanking: 0 };
  }

  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const chartData: Array<PropertyViewCompsChartDataPoint> = [];

  metricComps.forEach((monthComps, i) => {
    const date = subMonths(new Date(), metricComps.length - 1 - i);
    const formattedDate = format(date, 'MMM yyyy');
    const timestamp = date.getTime();

    const item: PropertyViewCompsChartDataPoint = {
      name: formattedDate,
      timestamp,
    };

    const displayedListings = [
      monthComps.selectedListing,
      ...monthComps.comps.filter((c) => displayedCompNames.includes(c.listingName)),
    ];
    const sorted = [...displayedListings].sort((a, b) => a.metricRank - b.metricRank);
    sorted.forEach((listing, index) => {
      item[listing.listingName] = index + 1;
    });

    chartData.push(item);
  });

  chartData.sort((a, b) => a.timestamp - b.timestamp);

  const mtdIndex = chartData.findIndex((data) => data.name === currentDateFormatted);
  if (mtdIndex > 0) {
    const selectedName = metricComps[0].selectedListing.listingName;
    const prevData = chartData[mtdIndex - 1];
    const mtdData = chartData[mtdIndex];
    const allNames = [selectedName, ...metricComps[0].comps.map((c) => c.listingName)];

    allNames.forEach((name) => {
      const mtdKey = `${name} month to date`;
      mtdData[mtdKey] = mtdData[name];
      prevData[mtdKey] = prevData[name];
      mtdData[name] = null;
    });
  }

  const selectedName = metricComps[0].selectedListing.listingName;
  const selectedRanks = chartData
    .map((d) => (d[selectedName] ?? d[`${selectedName} month to date`]) as number | null)
    .filter((r): r is number => r != null && r > 0);
  const averageViewRanking =
    selectedRanks.length > 0
      ? Math.round((selectedRanks.reduce((a, b) => a + b, 0) / selectedRanks.length) * 10) / 10
      : 0;

  return { chartData, averageViewRanking };
};
